import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useResponsive } from '../../../hooks/use-responsive';
import { ModelIconsAnimation } from './ModelIconsAnimation';
import { AuroraText } from '../../ui/aurora-text';
import { ShoppingCart, ArrowRight, ExternalLink } from 'lucide-react';

// iOS风格的弹簧动画配置
const iosSpringConfig = {
  type: "spring",
  stiffness: 300,
  damping: 25,
  mass: 0.8
};

export const SupportedModelsCard = (props = {}) => {
  // 添加错误边界保护
  let isMobile = false;
  try {
    const responsive = useResponsive();
    isMobile = responsive?.isMobile || false;
  } catch (error) {
    console.warn('useResponsive hook error:', error);
    isMobile = false;
  }
  
  // 从props中获取hasSaved状态，用于决定显示的副标题
  const { hasSaved = false } = props;
  const isWide = props.className?.includes('col-span-3');
  
  // 根据hasSaved状态决定副标题
  const subtitle = hasSaved ? "余额不足了？立即购买！" : "没有Key？立即购买！";
  
  return {
    className: props.className || "col-span-1 row-span-1 group relative overflow-hidden",
    name: subtitle, // 保持name为副标题，用于布局识别
    description: isWide ? "支持300+模型一键查询，包括GPT、Claude、Gemini等主流AI模型" : "支持300+模型",
    href: "https://shop.smallai.asia",
    cta: "立即购买",
    background: (
      <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-600">
        {/* 保持原有的模型图标动画效果 */}
        <ModelIconsAnimation />
        
        {/* 宽版本的额外装饰 */}
        {isWide && (
          <>
            {/* 左侧装饰 */}
            <div className="absolute left-6 top-1/2 -translate-y-1/2 opacity-10 transition-all duration-300 ease-out group-hover:opacity-20">
              <div className="w-16 h-16 bg-black dark:bg-white rounded-full" />
              <div className="w-12 h-12 bg-black dark:bg-white rounded-full absolute -top-4 -right-4 opacity-60" />
            </div>
            
            {/* 右侧装饰 */}
            <div className="absolute right-6 top-1/2 -translate-y-1/2 opacity-10 transition-all duration-300 ease-out group-hover:opacity-20">
              <div className="w-20 h-20 bg-black dark:bg-white rounded-lg rotate-12" />
              <div className="w-14 h-14 bg-black dark:bg-white rounded-lg absolute -top-6 -left-6 opacity-60 -rotate-12" />
            </div>
          </>
        )}
      </div>
    ),
    // 只保留和服务状态卡片一致的文字排版
    children: (
      <motion.div 
        className={`relative z-20 h-full flex flex-col justify-between ${isMobile ? 'p-4' : 'p-6'}`}
      >
        {isMobile ? (
          // 移动端引导式布局 - 和服务状态卡片保持一致
          <>
            {/* 顶部标题 */}
            <div className="flex-1 flex flex-col justify-center">
              <motion.div 
                className="text-gray-500 dark:text-gray-400 text-xs font-medium tracking-wider uppercase mb-2"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={iosSpringConfig}
              >
                {subtitle}
              </motion.div>
              
              {/* 商城名称 - 和服务名称保持相同的样式 */}
              <motion.div
                initial={{ opacity: 0, x: 20, scale: 0.95 }}
                animate={{ 
                  opacity: 1, 
                  x: 0, 
                  scale: 1,
                  transition: {
                    ...iosSpringConfig,
                    duration: 0.6
                  }
                }}
              >
                <AuroraText className="text-xl sm:text-2xl md:text-xl font-black text-gray-800 dark:text-gray-200 leading-tight">
                  SmallAi商城
                </AuroraText>
              </motion.div>
            </div>
          </>
        ) : (
          // 桌面端布局 - 和服务状态卡片保持一致
          <>
            <motion.div 
              className="text-gray-500 dark:text-gray-400 text-xs font-medium tracking-wider uppercase"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={iosSpringConfig}
            >
              {subtitle}
            </motion.div>  
            <motion.div 
              className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 transition-colors"
              animate={{
                x: [0, 5, 0]
              }}
              transition={{
                duration: 2.5,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            >
              <motion.div
                animate={{ rotate: [0, 15, -15, 0] }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              >
                <ExternalLink className="h-4 w-4" />
              </motion.div>
              立即购买
            </motion.div>
          </>
        )}
      </motion.div>
    ),
  };
}; 